import _ from 'lodash';
import moment from 'moment';
import coreModule from '../../core_module';

const DROPDOWN_MAX_ITEMS = 10;

const MODE_HINT = "hint";
const MODE_FILL_TAG = "filltag";
const MODE_FILL_SENSOR = "fillsensor";
const MODE_FILL_DATE = "filldate";

const SENSOR_KEYWORD = "sensor";
const DATE_KEYWORD = "date";
const TAG_KEYWORD = "tag";

const END_COLON = ':([^ "]*)$';
const END_COLON_QUOTE = ':"([^"]*)$';

const PARTIAL_SENSOR = SENSOR_KEYWORD + END_COLON + "|" + SENSOR_KEYWORD
  + END_COLON_QUOTE;
const PARTIAL_TAG = TAG_KEYWORD + END_COLON + "|" + TAG_KEYWORD
  + END_COLON_QUOTE;
const PARTIAL_DATE = DATE_KEYWORD + END_COLON + "|" + DATE_KEYWORD
  + END_COLON_QUOTE;

const reFilters = {
  sensor: new RegExp(PARTIAL_SENSOR),
  tag: new RegExp(PARTIAL_TAG),
  date: new RegExp(PARTIAL_DATE),
};

const DATE_LAST_WEEK = "Last week";
const DATE_LAST_MONTH = "Last month";
const DATE_LAST_YEAR = "Last year";

let prettyHints = {
  "sensor:" : "<span class='hint-keyword'>sensor:</span> "
    + "<span class='hint-description'>Filter sensors by name</span>",
  "date:" : "<span class='hint-keyword'>date:</span> "
    + "<span class='hint-description'>Filter experiments by date</span>",
  "tag:" : "<span class='hint-keyword'>tag:</span> "
    + "<span class='hint-description'>Filter experiment by tag</span>"
};

// Sidepane_search control
// This is the control for the input search box on top of the experiment list.
// It shows hints for filtering by tag: date: or sensor: keywords.
//
// The hint dropdown is always open when inputting, and has two major modes:
//   - HINT: show hint about which filters can be used (date, tag, or sensor)
//   - FILL: show potential values to filter by, for each filter type.
//
// In HINT mode, the list containst the 3 options and is static.
// Once you click a filter in HINT mode, we switch to FILL mode with potential
// suggestions to filter by.
//
// In FILL mode for the date: keyword, we show a few static options for date
// ranges. There is no highlighting or re-ordering, the list is static and
// an item has to be clicked, after which it will get replaced by the right
// date filter
//
// In FILL mode for the sensor: and tag: keywords, we first show a short list
// of tag or sensor options (<= 10). The user can keep typing to refine the
// search results. The user may also use quotes like tag:"Cold Flow".

// Typeahead library internals / Execution flow
// 1. typeahead.lookup()
// 2. source()
// 3. typeahead.process()
// 4. matcher()
// 5. sorter()
// 6. typeahead.render()
// 7. highlighter()
// After user selects an item:
// 8. typeahead.select()
// 9. updater()

// TODO
// - Expand something if filtering by sensor and nothing is expanded
// - Tag "pills" in dropdown (?)


export class SidepaneSearchCtrl {
  typeahead: any;
  inputElement: any;
  searchButton: any;
  model: any;
  text: any;
  dropdownMode: string;
  searchTerm: string;
  reOpenTimer: any;
  sensors: any;
  tagCache: any;
  onSearch: any;

  /** @ngInject **/
  constructor($element, private backendSrv) {
    this.inputElement = $element.find('input').first();
    this.searchButton = $element.find('.fa-search').first();

    this.inputElement.typeahead({
        source: this.source.bind(this),
        highlighter: this.highlighter.bind(this),
        sorter: (items) => { return items; },
        minLength: 0,
        items: DROPDOWN_MAX_ITEMS,
        updater: this.updater.bind(this),
        matcher: this.matcher.bind(this),
    });

    this.typeahead = this.inputElement.data('typeahead');

    // Modify lookup to show with empty query as well
    this.typeahead.lookup = function() {
      this.query = this.$element.val() || '';
      this.source(this.query, this.process.bind(this));
    };

    // Modify select to not enter anything if nothing is selected
    // TODO: if there is only one item in the dropdown, use that one
    // this will fix enter and tab behavior.
    this.typeahead.select = function() {
      var val = this.$menu.find('.active').attr('data-value');
      if (val) {
        this.$element.val(this.updater(val)).change();
      }
      return this.hide();
    };
  }

  // Parse they query, set the mode and the search term if there is one
  initDropdownMode(query) {
    let match;
    this.searchTerm = null;

    if (match = query.match(reFilters.sensor)) {
      this.dropdownMode = MODE_FILL_SENSOR;
      this.searchTerm = match[1] || match[2] || "";
    } else if (match = query.match(reFilters.tag)) {
      this.dropdownMode = MODE_FILL_TAG;
      this.searchTerm = match[1] || match[2] || "";
    } else if (match = query.match(reFilters.date)) {
      this.dropdownMode = MODE_FILL_DATE;
      this.searchTerm = match[1] || match[2] || "";
    } else {
      this.dropdownMode = MODE_HINT;
    }
  }

  source(query, callback) {
    this.initDropdownMode(query);

    switch (this.dropdownMode) {
      case MODE_HINT:
        callback(["sensor:", "date:", "tag:"]);
        break;

      case MODE_FILL_SENSOR:
        let sensorNames = Object.keys(this.sensors).map((key) => {
          return this.sensors[key].shortName;
        });
        callback(sensorNames);
        break;

      case MODE_FILL_DATE:
        callback([DATE_LAST_WEEK, DATE_LAST_MONTH, DATE_LAST_YEAR]);
        break;

      case MODE_FILL_TAG:
        if (this.tagCache !== undefined) {
          return callback(this.tagCache);
        }

        this.backendSrv.searchExperimentTags(this.searchTerm).then((tags) => {
          // If we can get all the tags in one call, cache them locally to avoid
          // making a network call every time. Otherwise, we have to make a call.
          if (this.tagCache === undefined
            && tags.results.length === tags.totalCount
            && this.searchTerm === "") {
            this.tagCache = tags.results;
          }
          callback(tags.results);
        });
        break;
    }
  }

  // This function decides whether to show "item" in the dropdown.
  matcher(item) {
    let mode = this.dropdownMode;
    if (mode !== MODE_FILL_SENSOR && mode !== MODE_FILL_TAG) {
      return true; // All other modes have a static dropdown
    }
    return item.toLowerCase().indexOf(this.searchTerm.toLowerCase()) !== -1;
  }

  // Highlight the partial match in the dropdown
  highlighter(item) {
    let mode = this.dropdownMode;
    if (mode === MODE_HINT) {
      // We abuse highlight mode to display HTML formatted hints.
      let hint = prettyHints[item];
      return hint ? hint : item;
    }

    let query = this.searchTerm;
    if (!query) {
      return item;
    }

    // This is the original typeahead highlighting code.
    query = query.replace(/[\-\[\]{}()*+?.,\\\^$|#\s]/g, '\\$&');
    if (!query) {
      return item;
    }
    return item.replace(new RegExp('(' + query + ')', 'ig'), function ($1, match) {
      return '<strong>' + match + '</strong>';
    });
  }

  // This function is called when the user selects an item from the dropdown
  // the return value is the new value of the input element.
  updater(text) {
    let currentText = this.inputElement.val();

    if (currentText.length === 0) {
      return text;
    }

    // In hint mode, just add the filter with a separating space before.
    if (this.dropdownMode === MODE_HINT) {
      return concatNoDup(currentText, text);
    }

    // When not in hint mode, we need to chop everything up to the colon and
    // replace, watching out for quotes in the process. And add a space after.
    currentText = currentText.substring(0,
      currentText.length - this.searchTerm.length);

    // Then remove a trailing quote if there is one
    if (currentText[currentText.length - 1] === '"') {
      currentText = currentText.substring(0, currentText.length - 1);
    }

    // Convert dates to a format that the backend understands
    if (text === DATE_LAST_WEEK) {
      text = ">" + moment().subtract(1, 'weeks').format('YYYY-MM-DD');
    } else if (text === DATE_LAST_MONTH) {
      text = ">" + moment().subtract(1, 'months').format('YYYY-MM-DD');
    } else if (text === DATE_LAST_YEAR) {
      text = ">" + moment().subtract(1, 'years').format('YYYY-MM-DD');
    }

    // Then insert the new text, wrapping in quotes if there is a space inside
    if (text.indexOf(" ") !== -1) {
      text = '"' + text + '"';
    }

    return currentText + text + " ";
  }

  inputClicked(event) {
    this.typeahead.lookup();
  }

  inputChanged() {
    clearTimeout(this.reOpenTimer);
    this.reOpenTimer = setTimeout(() => {
      if (!this.typeahead.shown) {
        this.typeahead.lookup();
      }
    }, 50);
  }

  inputFocused() {
    this.searchButton.addClass("active");
  }

  inputBlurred() {
    setTimeout(() => {
      if (this.inputElement.is(":focus") === false) {
        this.searchButton.removeClass("active");
      }
    }, 100);
  }

  searchButtonClicked(event) {
    event.preventDefault();
    this.onSearch({ $query: this.text });
  }

  // This is triggered by the typeahead library when enter is pressed with
  // nothing selected in the dropdown.
  inputSubmit(event) {
    this.onSearch({ $query: this.text });
  }

  clearClicked(event) {
    event.preventDefault();
    this.text = "";
    this.onSearch({ $query: "" });
  }
}

// concatNoDup returns the concatenation of two strings, but removes possible
// duplicates by searching for the longest substring of "query" it can find at
// the end of "str" and removing it before concatenating.
//   concatNoDup("sen", "sensor:") -> "sensor:"
function concatNoDup(str, query) {
  let lowstr = str.toLowerCase();
  let lowquery = query.toLowerCase();
  let i = query.length;

  while (i > 0 && !lowstr.endsWith(lowquery.substring(0, i))) {
      i--;
  }
  return str + query.substring(i);
}

const template = `
<input type="text"
  ng-model="ctrl.text"
  ng-change="ctrl.inputChanged()"
  class="gf-form-input"
  ng-click="ctrl.inputClicked($event)"
  ng-focus="ctrl.inputFocused()"
  ng-blur="ctrl.inputBlurred()"
  ng-submit="ctrl.inputSubmit($event)"
  spellcheck="false">
</input>
<i class="gf-form-input-icon fa fa-search"
    ng-click="ctrl.searchButtonClicked($event)"></i>
<i class="gf-form-input-icon fa fa-times"
    ng-if="ctrl.text"
    ng-click="ctrl.clearClicked($event)"></i>
`;


export function sidepaneSearchDirective() {
  return {
    restrict: 'E',
    template: template,
    controller: SidepaneSearchCtrl,
    bindToController: true,
    controllerAs: 'ctrl',
    scope: {
      model: '=',
      sensors: '<',
      onSearch: '&',
    },
  };
}

coreModule.directive('gfSidepaneSearch', sidepaneSearchDirective);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--2!./public/app/core/components/sidepane_search/sidepane_search.ts